import { Cookie } from '../helpers/cookie'
import { spread } from '../helpers/spread'
import { URI } from '../helpers/URI'

export const SESSION_COOKIE_KEY = 'mediavine_session'
export const DIRECT_KEY = 'DIRECT'

const COOKIE_TTL = 1800 // 30 minutes in seconds
export class Session {
  static singletonSession?: Session
  static cookie: Cookie<Session> = new Cookie({
    name: SESSION_COOKIE_KEY,
    maxAge: COOKIE_TTL
  })
  depth = 0
  referrer = ''
  wrapperVersionGroup: IVersionGroup
  videoVersionGroup: IVersionGroup

  constructor() {
    /** Skip our UTM cookies if GDPR enabled */
    if (window.$mediavine.web.model.gdpr === '1') {
      return
    }

    if (Session.singletonSession) {
      return Session.singletonSession
    }

    // Both the launcher and wrapper load Session, so we need to make it globally available
    // so they don't both increment the counter when they are first reference in their separate code bases.
    if (window.$mediavine.web.session) {
      Session.singletonSession = window.$mediavine.web.session
      return Session.singletonSession
    }

    const values = Session.cookie.value || {}
    spread(values).onto(this)
    const sessionReferrer = this.getReferrer()

    // Start the depth over at zero if the referrer changes.
    if (!this.referrer || this.referrer !== sessionReferrer) {
      this.depth = 0
    }
    this.depth++
    this.referrer = sessionReferrer

    Session.cookie.set(this)
    Session.singletonSession = this
    window.$mediavine.web.session = this
  }

  // Only used for testing
  static updateSession() {
    delete Session.singletonSession
    delete window.$mediavine.web.session
    Session.singletonSession = new Session()
    window.$mediavine.web.session = Session.singletonSession
    return Session.singletonSession
  }

  // Only used for testing
  static resetSession() {
    delete Session.singletonSession
    delete window.$mediavine.web.session
    Session.singletonSession = new Session()
    Session.singletonSession.depth = 0
    Session.singletonSession.referrer = ''
    Session.cookie.set(Session.singletonSession)
    window.$mediavine.web.session = Session.singletonSession
    return Session.singletonSession
  }

  setWrapperVersionGroup(wrapperVersionGroup: IVersionGroup) {
    this.wrapperVersionGroup = wrapperVersionGroup
    Session.cookie.set(this)
  }

  setVideoVersionGroup(videoVersionGroup: IVersionGroup) {
    this.videoVersionGroup = videoVersionGroup
    Session.cookie.set(this)
  }

  // We want to save the session-level referrer across intra-site navigation,
  // but if a new external referrer appears, we want to switch to the new one.
  getReferrer(): string {
    const pageHostname = URI.getHost(window.location.hostname)
    const referrer = document.referrer
    const referrerHostname = URI.getHost(referrer)
    const isIntraSiteNavigation = referrerHostname.includes(pageHostname)

    if (!this.referrer) {
      if (referrerHostname === '' || isIntraSiteNavigation) {
        return DIRECT_KEY
      }

      return referrer
    } else {
      // If the current referrer is different from the saved session-referrer AND NOT from intra-site navigation,
      // use the current referrer
      if (referrer && this.referrer !== referrer && !isIntraSiteNavigation) {
        return referrer
      }

      return this.referrer
    }
  }
}

export default new Session()
